import dayjs from '../../util/dayjs';

import { fetchCurrentUser, updateUserProfile } from '../../ducks/user.duck';
import {
  passActivate,
  passCheck,
  stripeCreateCustomer,
  stripeCreatePortalSession,
  stripeSearchProduct,
  subscriptionCancel,
  subscriptionCreateSession,
  subscriptionFetch,
  subscriptionFulfill,
  subscriptionNextPeriodCheckout,
  subscriptionNextPeriodFulfill,
  subscriptionPassChangeCheckout,
  subscriptionReconcileExisting,
  subscriptionRenewalVoucherApply,
  subscriptionRenewalVoucherRemove,
  subscriptionSchedule,
  subscriptionUpgradeCheckout,
  subscriptionUpgradeFulfill,
} from '../../util/api';
import { CHECKOUT_TYPE, ORIGIN_CONTEXT } from '../../util/checkoutContext';
import { collectTrackingContext } from '../../util/consent/trackingContext';
import { getLocaleFromPath } from '../../util/locale';
import { logError } from '../../util/log';
import { isCordova } from '../../util/mobile/deviceDetection';
import { computeStripeReturnUrl } from '../../util/mobile/externalCheckout';
import reducerRegistry from '../../ducks/reducerRegistry';
import {
  CHECK_PASS_VOUCHER_ERROR,
  CHECK_PASS_VOUCHER_REQUEST,
  CHECK_PASS_VOUCHER_SUCCESS,
} from '../../ducks/passVoucher';

// ================ Action types ================ //

export const ACTIVATE_PASS_REQUEST = 'app/PassSettingsPage/ACTIVATE_PASS_REQUEST';
export const ACTIVATE_PASS_SUCCESS = 'app/PassSettingsPage/ACTIVATE_PASS_SUCCESS';
export const ACTIVATE_PASS_ERROR = 'app/PassSettingsPage/ACTIVATE_PASS_ERROR';

export const ACTIVATE_PASS_WITH_SUBSCRIPTION_REQUEST = 'app/PassSettingsPage/ACTIVATE_PASS_WITH_SUBSCRIPTION_REQUEST';
export const ACTIVATE_PASS_WITH_SUBSCRIPTION_SUCCESS = 'app/PassSettingsPage/ACTIVATE_PASS_WITH_SUBSCRIPTION_SUCCESS';
export const ACTIVATE_PASS_WITH_SUBSCRIPTION_ERROR = 'app/PassSettingsPage/ACTIVATE_PASS_WITH_SUBSCRIPTION_ERROR';


export const FETCH_SUBSCRIPTIONS_REQUEST = 'app/PassSettingsPage/FETCH_SUBSCRIPTIONS_REQUEST';
export const FETCH_SUBSCRIPTIONS_SUCCESS = 'app/PassSettingsPage/FETCH_SUBSCRIPTIONS_SUCCESS';
export const FETCH_SUBSCRIPTIONS_ERROR = 'app/PassSettingsPage/FETCH_SUBSCRIPTIONS_ERROR';

export const FETCH_SUBSCRIPTION_PRODUCTS_REQUEST = 'app/PassSettingsPage/FETCH_SUBSCRIPTION_PRODUCTS_REQUEST';
export const FETCH_SUBSCRIPTION_PRODUCTS_SUCCESS = 'app/PassSettingsPage/FETCH_SUBSCRIPTION_PRODUCTS_SUCCESS';
export const FETCH_SUBSCRIPTION_PRODUCTS_ERROR = 'app/PassSettingsPage/FETCH_SUBSCRIPTION_PRODUCTS_ERROR';

export const CREATE_PORTAL_SESSION_REQUEST = 'app/PassSettingsPage/CREATE_PORTAL_SESSION';
export const CREATE_PORTAL_SESSION_SUCCESS = 'app/PassSettingsPage/CREATE_PORTAL_SESSION_SUCCESS';
export const CREATE_PORTAL_SESSION_ERROR = 'app/PassSettingsPage/CREATE_PORTAL_SESSION_ERROR';

export const RESET_CHECKOUT_SESSION = 'app/PassSettingsPage/RESET_CHECKOUT_SESSION';
export const CREATE_STRIPE_CHECKOUT_SESSION_REQUEST = 'app/PassSettingsPage/CREATE_STRIPE_CHECKOUT_SESSION_REQUEST';
export const CREATE_STRIPE_CHECKOUT_SESSION_SUCCESS = 'app/PassSettingsPage/CREATE_STRIPE_CHECKOUT_SESSION_SUCCESS';
export const CREATE_STRIPE_CHECKOUT_SESSION_ERROR = 'app/PassSettingsPage/CREATE_STRIPE_CHECKOUT_SESSION_ERROR';

export const FULFILL_STRIPE_CHECKOUT_SESSION_REQUEST = 'app/PassSettingsPage/FULFILL_STRIPE_CHECKOUT_SESSION_REQUEST';
export const FULFILL_STRIPE_CHECKOUT_SESSION_SUCCESS = 'app/PassSettingsPage/FULFILL_STRIPE_CHECKOUT_SESSION_SUCCESS';
export const FULFILL_STRIPE_CHECKOUT_SESSION_ERROR = 'app/PassSettingsPage/FULFILL_STRIPE_CHECKOUT_SESSION_ERROR';

export const NEXT_PERIOD_CHECKOUT_REQUEST = 'app/PassSettingsPage/NEXT_PERIOD_CHECKOUT_REQUEST';
export const NEXT_PERIOD_CHECKOUT_SUCCESS = 'app/PassSettingsPage/NEXT_PERIOD_CHECKOUT_SUCCESS';
export const NEXT_PERIOD_CHECKOUT_ERROR = 'app/PassSettingsPage/NEXT_PERIOD_CHECKOUT_ERROR';

// One in-progress flag and one error shared by apply and remove: the UI never
// runs both at once.
export const RENEWAL_VOUCHER_REQUEST = 'app/PassSettingsPage/RENEWAL_VOUCHER_REQUEST';
export const RENEWAL_VOUCHER_SUCCESS = 'app/PassSettingsPage/RENEWAL_VOUCHER_SUCCESS';
export const RENEWAL_VOUCHER_ERROR = 'app/PassSettingsPage/RENEWAL_VOUCHER_ERROR';
export const RENEWAL_VOUCHER_ERROR_RESET = 'app/PassSettingsPage/RENEWAL_VOUCHER_ERROR_RESET';

export const NEXT_PERIOD_FULFILL_REQUEST = 'app/PassSettingsPage/NEXT_PERIOD_FULFILL_REQUEST';
export const NEXT_PERIOD_FULFILL_SUCCESS = 'app/PassSettingsPage/NEXT_PERIOD_FULFILL_SUCCESS';
export const NEXT_PERIOD_FULFILL_ERROR = 'app/PassSettingsPage/NEXT_PERIOD_FULFILL_ERROR';

export const PASS_CHANGE_CHECKOUT_REQUEST = 'app/PassSettingsPage/PASS_CHANGE_CHECKOUT_REQUEST';
export const PASS_CHANGE_CHECKOUT_SUCCESS = 'app/PassSettingsPage/PASS_CHANGE_CHECKOUT_SUCCESS';
export const PASS_CHANGE_CHECKOUT_ERROR = 'app/PassSettingsPage/PASS_CHANGE_CHECKOUT_ERROR';

export const PASS_UPGRADE_CHECKOUT_REQUEST = 'app/PassSettingsPage/PASS_UPGRADE_CHECKOUT_REQUEST';
export const PASS_UPGRADE_CHECKOUT_SUCCESS = 'app/PassSettingsPage/PASS_UPGRADE_CHECKOUT_SUCCESS';
export const PASS_UPGRADE_CHECKOUT_ERROR = 'app/PassSettingsPage/PASS_UPGRADE_CHECKOUT_ERROR';

export const PASS_UPGRADE_FULFILL_REQUEST = 'app/PassSettingsPage/PASS_UPGRADE_FULFILL_REQUEST';
export const PASS_UPGRADE_FULFILL_SUCCESS = 'app/PassSettingsPage/PASS_UPGRADE_FULFILL_SUCCESS';
export const PASS_UPGRADE_FULFILL_ERROR = 'app/PassSettingsPage/PASS_UPGRADE_FULFILL_ERROR';

export const RECONCILE_SUBSCRIPTION_REQUEST = 'app/PassSettingsPage/RECONCILE_SUBSCRIPTION_REQUEST';
export const RECONCILE_SUBSCRIPTION_SUCCESS = 'app/PassSettingsPage/RECONCILE_SUBSCRIPTION_SUCCESS';
export const RECONCILE_SUBSCRIPTION_ERROR = 'app/PassSettingsPage/RECONCILE_SUBSCRIPTION_ERROR';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.PassSettingsPage} */
const initialState = {
  checkPassVoucherInProgress: false,
  checkPassVoucherError: null,
  activePassInProgress: false,
  activePassError: null,
  subscription: null,
  subscriptionFetched: false,
  fetchSubscriptionInProgress: false,
  fetchSubscriptionsRequestId: 0,
  fetchSubscriptionsError: null,
  subscriptionProducts: [],
  fetchSubscriptionProductsInProgress: false,
  fetchSubscriptionProductsError: null,
  createPortalSessionInProgress: false,
  createPortalSessionError: null,
  activePassWithSubscriptionInProgress: false,
  activePassWithSubscriptionError: null,
  stripeCheckoutSessionInProgress: null,
  stripeCheckoutSessionError: null,
  fulfillStripeCheckoutSessionInProgress: false,
  fulfillStripeCheckoutSessionError: null,
  nextPeriodCheckoutInProgress: false,
  nextPeriodCheckoutError: null,
  renewalVoucherInProgress: false,
  renewalVoucherError: null,
  nextPeriodFulfillInProgress: false,
  nextPeriodFulfillError: null,
  reconcileSubscriptionInProgress: false,
  reconcileSubscriptionError: null,
  passChangeCheckoutInProgress: false,
  passChangeCheckoutError: null,
  passUpgradeCheckoutInProgress: false,
  passUpgradeCheckoutError: null,
  passUpgradeFulfillInProgress: false,
  passUpgradeFulfillError: null,
};

export default function passSettingsPageReducer(state = initialState, action = {}) {
  const { type } = action;
  switch (type) {
    case CHECK_PASS_VOUCHER_REQUEST:
      return {
        ...state,
        checkPassVoucherInProgress: true,
        checkPassVoucherError: null,
      };
    case CHECK_PASS_VOUCHER_SUCCESS:
      return { ...state, checkPassVoucherInProgress: false };
    case CHECK_PASS_VOUCHER_ERROR:
      return {
        ...state,
        checkPassVoucherInProgress: false,
        checkPassVoucherError: action.payload,
      };

    case ACTIVATE_PASS_REQUEST:
      return { ...state, activePassInProgress: true, activePassError: null };
    case ACTIVATE_PASS_SUCCESS:
      return { ...state, activePassInProgress: false };
    case ACTIVATE_PASS_ERROR:
      return { ...state, activePassInProgress: false, activePassError: action.payload };

    case FETCH_SUBSCRIPTIONS_REQUEST:
      return {
        ...state,
        fetchSubscriptionInProgress: true,
        fetchSubscriptionsRequestId: action.payload,
        fetchSubscriptionsError: null,
      };
    case FETCH_SUBSCRIPTIONS_SUCCESS:
      return {
        ...state,
        fetchSubscriptionInProgress: false,
        subscription: action.payload,
        subscriptionFetched: true,
      };
    case FETCH_SUBSCRIPTIONS_ERROR:
      return { ...state, fetchSubscriptionInProgress: false, fetchSubscriptionsError: action.payload };

    case FETCH_SUBSCRIPTION_PRODUCTS_REQUEST:
      return { ...state, fetchSubscriptionProductsInProgress: true, fetchSubscriptionProductsError: null };
    case FETCH_SUBSCRIPTION_PRODUCTS_SUCCESS:
      return {
        ...state,
        fetchSubscriptionProductsInProgress: false,
        subscriptionProducts: [...state.subscriptionProducts, ...action.payload],
      };
    case FETCH_SUBSCRIPTION_PRODUCTS_ERROR:
      return { ...state, fetchSubscriptionProductsInProgress: false, fetchSubscriptionProductsError: action.payload };

    case CREATE_PORTAL_SESSION_REQUEST:
      return { ...state, createPortalSessionInProgress: true, createPortalSessionError: null };
    case CREATE_PORTAL_SESSION_SUCCESS:
      return { ...state, createPortalSessionInProgress: false };
    case CREATE_PORTAL_SESSION_ERROR:
      return { ...state, createPortalSessionInProgress: false, createPortalSessionError: action.payload };

    case ACTIVATE_PASS_WITH_SUBSCRIPTION_REQUEST:
      return { ...state, activePassWithSubscriptionInProgress: true, activePassWithSubscriptionError: null };
    case ACTIVATE_PASS_WITH_SUBSCRIPTION_SUCCESS:
      return { ...state, activePassWithSubscriptionInProgress: false };
    case ACTIVATE_PASS_WITH_SUBSCRIPTION_ERROR:
      return { ...state, activePassWithSubscriptionInProgress: false, activePassWithSubscriptionError: action.payload };

    case RESET_CHECKOUT_SESSION:
      return { ...state, stripeCheckoutSessionInProgress: false, stripeCheckoutSessionError: null };
    case CREATE_STRIPE_CHECKOUT_SESSION_REQUEST:
      return { ...state, stripeCheckoutSessionInProgress: true, stripeCheckoutSessionError: null };
    case CREATE_STRIPE_CHECKOUT_SESSION_SUCCESS:
      return { ...state, stripeCheckoutSessionInProgress: false };
    case CREATE_STRIPE_CHECKOUT_SESSION_ERROR:
      return { ...state, stripeCheckoutSessionInProgress: false, stripeCheckoutSessionError: action.payload };

    case FULFILL_STRIPE_CHECKOUT_SESSION_REQUEST:
      return { ...state, fulfillStripeCheckoutSessionInProgress: true, fulfillStripeCheckoutSessionError: null };
    case FULFILL_STRIPE_CHECKOUT_SESSION_SUCCESS:
      return { ...state, fulfillStripeCheckoutSessionInProgress: false };
    case FULFILL_STRIPE_CHECKOUT_SESSION_ERROR:
      return {
        ...state,
        fulfillStripeCheckoutSessionInProgress: false,
        fulfillStripeCheckoutSessionError: action.payload,
      };

    case NEXT_PERIOD_CHECKOUT_REQUEST:
      return { ...state, nextPeriodCheckoutInProgress: true, nextPeriodCheckoutError: null };
    case NEXT_PERIOD_CHECKOUT_SUCCESS:
      return { ...state, nextPeriodCheckoutInProgress: false };
    case NEXT_PERIOD_CHECKOUT_ERROR:
      return { ...state, nextPeriodCheckoutInProgress: false, nextPeriodCheckoutError: action.payload };

    case RENEWAL_VOUCHER_REQUEST:
      return { ...state, renewalVoucherInProgress: true, renewalVoucherError: null };
    case RENEWAL_VOUCHER_SUCCESS:
      return { ...state, renewalVoucherInProgress: false };
    case RENEWAL_VOUCHER_ERROR:
      return { ...state, renewalVoucherInProgress: false, renewalVoucherError: action.payload };
    case RENEWAL_VOUCHER_ERROR_RESET:
      return { ...state, renewalVoucherError: null };

    case NEXT_PERIOD_FULFILL_REQUEST:
      return { ...state, nextPeriodFulfillInProgress: true, nextPeriodFulfillError: null };
    case NEXT_PERIOD_FULFILL_SUCCESS:
      return { ...state, nextPeriodFulfillInProgress: false };
    case NEXT_PERIOD_FULFILL_ERROR:
      return { ...state, nextPeriodFulfillInProgress: false, nextPeriodFulfillError: action.payload };

    case PASS_CHANGE_CHECKOUT_REQUEST:
      return { ...state, passChangeCheckoutInProgress: true, passChangeCheckoutError: null };
    case PASS_CHANGE_CHECKOUT_SUCCESS:
      return { ...state, passChangeCheckoutInProgress: false };
    case PASS_CHANGE_CHECKOUT_ERROR:
      return { ...state, passChangeCheckoutInProgress: false, passChangeCheckoutError: action.payload };

    case PASS_UPGRADE_CHECKOUT_REQUEST:
      return { ...state, passUpgradeCheckoutInProgress: true, passUpgradeCheckoutError: null };
    case PASS_UPGRADE_CHECKOUT_SUCCESS:
      return { ...state, passUpgradeCheckoutInProgress: false };
    case PASS_UPGRADE_CHECKOUT_ERROR:
      return { ...state, passUpgradeCheckoutInProgress: false, passUpgradeCheckoutError: action.payload };

    case PASS_UPGRADE_FULFILL_REQUEST:
      return { ...state, passUpgradeFulfillInProgress: true, passUpgradeFulfillError: null };
    case PASS_UPGRADE_FULFILL_SUCCESS:
      return { ...state, passUpgradeFulfillInProgress: false };
    case PASS_UPGRADE_FULFILL_ERROR:
      return { ...state, passUpgradeFulfillInProgress: false, passUpgradeFulfillError: action.payload };

    case RECONCILE_SUBSCRIPTION_REQUEST:
      return { ...state, reconcileSubscriptionInProgress: true, reconcileSubscriptionError: null };
    case RECONCILE_SUBSCRIPTION_SUCCESS:
      return { ...state, reconcileSubscriptionInProgress: false };
    case RECONCILE_SUBSCRIPTION_ERROR:
      return { ...state, reconcileSubscriptionInProgress: false, reconcileSubscriptionError: action.payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const activatePassRequest = () => ({ type: ACTIVATE_PASS_REQUEST });
export const activatePassSuccess = () => ({ type: ACTIVATE_PASS_SUCCESS });
export const activatePassError = (error) => ({
  type: ACTIVATE_PASS_ERROR,
  payload: error,
});

export const fetchSubscriptionsRequest = (requestId) => ({
  type: FETCH_SUBSCRIPTIONS_REQUEST,
  payload: requestId,
});
export const fetchSubscriptionsSuccess = (subscription) => ({
  type: FETCH_SUBSCRIPTIONS_SUCCESS,
  payload: subscription,
});
export const fetchSubscriptionsError = (error) => ({
  type: FETCH_SUBSCRIPTIONS_ERROR,
  payload: error,
});

export const fetchSubscriptionProductsRequest = () => ({ type: FETCH_SUBSCRIPTION_PRODUCTS_REQUEST });
export const fetchSubscriptionProductsSuccess = (subscriptionProducts) => ({
  type: FETCH_SUBSCRIPTION_PRODUCTS_SUCCESS,
  payload: subscriptionProducts,
});
export const fetchSubscriptionProductsError = (error) => ({
  type: FETCH_SUBSCRIPTION_PRODUCTS_ERROR,
  payload: error,
});

export const createPortalSessionRequest = () => ({ type: CREATE_PORTAL_SESSION_REQUEST });
export const createPortalSessionSuccess = () => ({
  type: CREATE_PORTAL_SESSION_SUCCESS,
});
export const createPortalSessionError = (error) => ({
  type: CREATE_PORTAL_SESSION_ERROR,
  payload: error,
});

export const activatePassWithSubscriptionRequest = () => ({ type: ACTIVATE_PASS_WITH_SUBSCRIPTION_REQUEST });
export const activatePassWithSubscriptionSuccess = () => ({ type: ACTIVATE_PASS_WITH_SUBSCRIPTION_SUCCESS });
export const activatePassWithSubscriptionError = (error) => ({
  type: ACTIVATE_PASS_WITH_SUBSCRIPTION_ERROR,
  payload: error,
});

export const resetCheckoutSession = () => ({ type: RESET_CHECKOUT_SESSION });
export const createStripeCheckoutSessionRequest = () => ({ type: CREATE_STRIPE_CHECKOUT_SESSION_REQUEST });
export const createStripeCheckoutSessionSuccess = () => ({ type: CREATE_STRIPE_CHECKOUT_SESSION_SUCCESS });
/**
 * Stores the checkout failure the way its readers need it: `code` is the
 * server's structured reason (`subscription-already-active`) or `generic`,
 * `subscriptionId` names the blocking subscription for the reconcile path,
 * `key` carries a voucher rejection from the server's own re-validation.
 * Same shape as passUpgradeCheckoutError — the earlier `error.data || generic`
 * dropped the code, so the already-active case never reached the UI.
 *
 * @param {any} e rejected error from subscriptionCreateSession
 */
export const createStripeCheckoutSessionError = (e) => {
  const error = e?.error || {};
  return {
    type: CREATE_STRIPE_CHECKOUT_SESSION_ERROR,
    payload: {
      code: error.code || 'generic',
      subscriptionId: error.subscriptionId ?? null,
      validFrom: error.validFrom ?? null,
      key: e?.data?.key ?? null,
      raw: e,
    },
  };
};

export const fulfillStripeCheckoutSessionRequest = () => ({ type: FULFILL_STRIPE_CHECKOUT_SESSION_REQUEST });
export const fulfillStripeCheckoutSessionSuccess = () => ({ type: FULFILL_STRIPE_CHECKOUT_SESSION_SUCCESS });
export const fulfillStripeCheckoutSessionError = (error) => ({
  type: FULFILL_STRIPE_CHECKOUT_SESSION_ERROR,
  payload: error.data || { error: 'generic' },
});

export const nextPeriodCheckoutRequest = () => ({ type: NEXT_PERIOD_CHECKOUT_REQUEST });
export const nextPeriodCheckoutSuccess = () => ({ type: NEXT_PERIOD_CHECKOUT_SUCCESS });
export const nextPeriodCheckoutError = (error) => ({
  type: NEXT_PERIOD_CHECKOUT_ERROR,
  payload: error.data || { error: 'generic' },
});

export const renewalVoucherRequest = () => ({ type: RENEWAL_VOUCHER_REQUEST });
export const renewalVoucherSuccess = () => ({ type: RENEWAL_VOUCHER_SUCCESS });
export const renewalVoucherError = (error) => ({
  type: RENEWAL_VOUCHER_ERROR,
  payload: error.data || { error: 'generic' },
});
export const resetRenewalVoucherError = () => ({ type: RENEWAL_VOUCHER_ERROR_RESET });

export const nextPeriodFulfillRequest = () => ({ type: NEXT_PERIOD_FULFILL_REQUEST });
export const nextPeriodFulfillSuccess = () => ({ type: NEXT_PERIOD_FULFILL_SUCCESS });
export const nextPeriodFulfillError = (error) => ({
  type: NEXT_PERIOD_FULFILL_ERROR,
  payload: error.data || { error: 'generic' },
});

export const passChangeCheckoutRequest = () => ({ type: PASS_CHANGE_CHECKOUT_REQUEST });
export const passChangeCheckoutSuccess = () => ({ type: PASS_CHANGE_CHECKOUT_SUCCESS });
export const passChangeCheckoutError = (payload) => ({
  type: PASS_CHANGE_CHECKOUT_ERROR,
  payload,
});

export const passUpgradeCheckoutRequest = () => ({ type: PASS_UPGRADE_CHECKOUT_REQUEST });
export const passUpgradeCheckoutSuccess = () => ({ type: PASS_UPGRADE_CHECKOUT_SUCCESS });
export const passUpgradeCheckoutError = (payload) => ({
  type: PASS_UPGRADE_CHECKOUT_ERROR,
  payload,
});

export const passUpgradeFulfillRequest = () => ({ type: PASS_UPGRADE_FULFILL_REQUEST });
export const passUpgradeFulfillSuccess = () => ({ type: PASS_UPGRADE_FULFILL_SUCCESS });
export const passUpgradeFulfillError = (payload) => ({
  type: PASS_UPGRADE_FULFILL_ERROR,
  payload,
});

export const reconcileSubscriptionRequest = () => ({ type: RECONCILE_SUBSCRIPTION_REQUEST });
export const reconcileSubscriptionSuccess = () => ({ type: RECONCILE_SUBSCRIPTION_SUCCESS });
export const reconcileSubscriptionError = (payload) => ({
  type: RECONCILE_SUBSCRIPTION_ERROR,
  payload,
});

// ================ Thunks ================ //

export const activatePass = ({
  voucherCode,
  annualPassLicensePlate,
  rentalStartDate = null,
  rentalEndDate = null,
}) => async (dispatch) => {
  dispatch(activatePassRequest());
  try {
    const response = await passActivate({
      code: voucherCode,
      annualPassLicensePlate,
      rentalStartDate,
      rentalEndDate,
    });

    if (response.data.valid) {
      dispatch(activatePassSuccess());
      dispatch(fetchCurrentUser());
      return response.data;
    }

    dispatch(activatePassError(response.data));
    return response.data;
  } catch (e) {
    logError(e, 'activate-annual-pass', { voucherCode, annualPassLicensePlate });
    dispatch(activatePassError(e));
    return e;
  }
};

/** @type {(locale: string) => import('../../../types/vansite').AppThunkAction} */
export const fetchSubscriptionProducts = (locale) => async (dispatch, getState) => {
  const { fetchSubscriptionProductsInProgress, subscriptionProducts } = getState().PassSettingsPage;
  if (fetchSubscriptionProductsInProgress) return;
  if (subscriptionProducts.length > 0) return;

  try {
    dispatch(fetchSubscriptionProductsRequest());
    const response = await stripeSearchProduct({
      query: `active: 'true' AND metadata['locale']: '${locale}' AND metadata['pass']: 'true'`,
    });
    dispatch(fetchSubscriptionProductsSuccess(response.data));
    return response.data;
  } catch (e) {
    logError(e, 'fetch-subscription-products');
    dispatch(fetchSubscriptionProductsError(e));
  }
};

// Stripe's subscription list returns any non-canceled subscription, including
// `incomplete` / `incomplete_expired` ones left over from abandoned checkouts.
// Only these statuses count as the user actually HAVING a subscription — anything
// else means "no active subscription" so the pass page shows the pricing table.
const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'past_due', 'unpaid'];

// Pick the genuinely active subscription, not just the first one Stripe
// returns — an `incomplete` leftover must resolve to "no subscription" so the
// overview doesn't spin forever (subscription && !pass) and the pricing table
// can render.
const findActiveSubscription = (subscriptions) =>
  (subscriptions || []).find((s) => ACTIVE_SUBSCRIPTION_STATUSES.includes(s.status)) || null;

// A cached `null` is only trustworthy for a user without a linked subscription.
// With `annualPass.subscriptionId` set it is a stale pre-fulfil answer (or a
// dropped refresh) that would otherwise stick until a full reload.
const canReuseFetchedSubscription = ({ subscriptionFetched, subscription }, currentUser) =>
  subscriptionFetched
  && !(!subscription && currentUser?.attributes?.profile?.privateData?.annualPass?.subscriptionId);

// Every fetch that actually hits the API takes the next id from the slice state
// (per store, never module-level: SSR serves many users from one process); only
// the newest one may write its result, so a slow pre-fulfil response can never
// overwrite the refresh that was started after it.
// Non-refresh calls piggyback on a request already in flight and reuse a
// trustworthy cache; a refresh always goes to the API.
const shouldSkipSubscriptionsFetch = (pageState, currentUser, refresh) =>
  !refresh
  && (pageState.fetchSubscriptionInProgress || canReuseFetchedSubscription(pageState, currentUser));

// `data` is Stripe's raw list (null without a Stripe customer), `subscription`
// the one that counts as the user's active subscription.
const loadSubscriptions = async (currentUser) => {
  const stripeCustomer = currentUser.attributes.profile.privateData.stripeCustomer;
  if (!stripeCustomer) return { data: null, subscription: null };

  const response = await subscriptionFetch({ customerId: stripeCustomer.id });
  return { data: response.data, subscription: findActiveSubscription(response.data) };
};

/** @type {(refresh?: boolean) => import('../../../types/vansite').AppThunkAction} */
export const fetchSubscriptions = (refresh = false) => async (dispatch, getState) => {
  const pageState = getState().PassSettingsPage;
  if (shouldSkipSubscriptionsFetch(pageState, getState().user.currentUser, refresh)) return;

  const requestId = (pageState.fetchSubscriptionsRequestId || 0) + 1;
  const isLatest = () => getState().PassSettingsPage.fetchSubscriptionsRequestId === requestId;

  dispatch(fetchSubscriptionsRequest(requestId));
  try {
    const { data, subscription } = await loadSubscriptions(getState().user.currentUser);
    if (isLatest()) dispatch(fetchSubscriptionsSuccess(subscription));
    return data;
  } catch (e) {
    logError(e, 'fetch-subscriptions');
    if (isLatest()) dispatch(fetchSubscriptionsError(e));
  }
};

/** @type {() => import('../../../types/vansite').AppThunkAction} */
export const createPortalSession = () => async (dispatch, getState) => {
  const { currentUser } = getState().user;
  const { createPortalSessionInProgress } = getState().PassSettingsPage;
  if (createPortalSessionInProgress || !currentUser) return;

  dispatch(createPortalSessionRequest());

  try {
    let stripeCustomerId = currentUser.attributes.profile?.privateData?.stripeCustomer?.id;
    if (!stripeCustomerId) {
      const response = await stripeCreateCustomer({ user: currentUser });
      const { stripeCustomer } = response.data;
      await dispatch(updateUserProfile({
        privateData: { stripeCustomer },
      }));
      stripeCustomerId = stripeCustomer.id;
    }
    const returnUrl = computeStripeReturnUrl();

    const response = await stripeCreatePortalSession({ stripeCustomerId, returnUrl });
    dispatch(createPortalSessionSuccess());
    return response.data;
  } catch (e) {
    logError(e, 'create-portal-session');
    dispatch(createPortalSessionError(e));
  }
};

// Checkout refusals that retrying can never fix, mapped to the activation
// form's own message. Everything else stays `checkout-failed` ("try again").
// - subscription-already-active: a pass subscription is already running.
// - annual-pass-not-activatable: the server would not price the code at 0 €
//   for its first year and refuses rather than opening a full-price checkout
//   for a code that is already paid — the customer needs support, not a retry.
// - annual-pass-held-by-other-customer: the code is already bound to another
//   account.
// - annual-pass-not-yet-valid: the code's start_date lies ahead, it works from then on.
const ACTIVATION_FAILURE_BY_CHECKOUT_CODE = new Map([
  ['subscription-already-active', 'subscription-already-active'],
  ['annual-pass-not-activatable', 'not-allowed-profile'],
  ['annual-pass-held-by-other-customer', 'already-used'],
  ['annual-pass-not-yet-valid', 'not-yet-valid'],
]);

/**
 * @param {{ code?: string } | null} checkoutError reducer field `stripeCheckoutSessionError`
 * @returns {string} error code the PassActivationForm renders
 */
const toActivationFailureCode = (checkoutError) =>
  ACTIVATION_FAILURE_BY_CHECKOUT_CODE.get(checkoutError?.code) || 'checkout-failed';

/**
 * The schedule endpoint (code linked onto a running pass) refuses a code with the
 * checkout's codes as a plain-JSON 400, rehydrated by postWeb as `e.error`. Those get
 * the form's own message; any other failure stays the raw error.
 *
 * @param {any} e rejected error
 * @returns {object} payload for activatePassWithSubscriptionError
 */
const toThrownActivationFailure = (e) =>
  ACTIVATION_FAILURE_BY_CHECKOUT_CODE.has(e?.error?.code)
    ? { code: toActivationFailureCode(e.error), validFrom: e.error.validFrom }
    : e;

/** @type {(code: string, annualPassLicensePlate: string, intl: Intl) => import('../../../types/vansite').AppThunkAction<sting | >} */
export const activePassWithSubscription = (code, annualPassLicensePlate, intl) => async (dispatch, getState) => {
  const { activePassWithSubscriptionInProgress, subscription } = getState().PassSettingsPage;
  if (activePassWithSubscriptionInProgress) return;

  try {
    dispatch(activatePassWithSubscriptionRequest());
    const response = await passCheck({ code });
    const { valid, result, reason, validFrom } = response.data;
    if (valid) {
      const { metadata } = result;

      const isFlexPass =
        !!metadata.flexPass || !!(metadata.rentalStartDate && metadata.rentalEndDate);

      if (isFlexPass) {
        // FlexPass is already paid during purchase — skip Stripe subscription
        // and register the voucher on the user directly. The server resolves
        // rentalStartDate/rentalEndDate from the voucher metadata.
        const activationResult = await dispatch(activatePass({ voucherCode: code }));
        if (activationResult?.valid) {
          dispatch(activatePassWithSubscriptionSuccess());
          return 'activated';
        }
        // Surface the server's rejection (e.g. already-used, rental-too-long)
        // instead of silently reporting success.
        dispatch(
          activatePassWithSubscriptionError(
            activationResult && activationResult.valid === false
              ? { code: activationResult.code, validFrom: activationResult.validFrom }
              : activationResult
          )
        );
        return undefined;
      }

      const isTwentyFivePass = !!metadata.annualTwentyFivePass;
      const isAnnualPass = !!metadata.annualPass;
      const isFamilyPass = isAnnualPass && metadata.included?.guestCount === 5;

      const { subscriptionProducts } = getState().PassSettingsPage;

      let priceId = null;
      if (isTwentyFivePass) {
        priceId = subscriptionProducts.find(p => p.metadata.twentyFivePass === 'true').default_price.id;
      } else if (isFamilyPass) {
        priceId = subscriptionProducts.find(p => p.metadata.twentyFivePass === undefined && p.metadata.guestCount === '5').default_price.id;
      } else if (isAnnualPass) {
        priceId = subscriptionProducts.find(p => p.metadata.twentyFivePass === undefined && p.metadata.guestCount === '2').default_price.id;
      }

      const currentUser = getState().user.currentUser;
      let stripeCustomer = currentUser.attributes.profile.privateData.stripeCustomer;
      if (!stripeCustomer) {
        const response = await stripeCreateCustomer({ user: currentUser });
        await dispatch(updateUserProfile({
          privateData: { stripeCustomer: response.data.stripeCustomer },
        }));
      }

      if (subscription?.status === 'incomplete') {
        await subscriptionCancel({ subscriptionId: subscription.id });
      }

      if (subscription?.status === 'active') {
        await subscriptionSchedule({
          priceId,
          subscriptionId: subscription.id,
          newAnnualPassCode: code,
          annualPassLicensePlate,
        });
        dispatch(activatePassWithSubscriptionSuccess());
        await dispatch(fetchSubscriptions(true));
        return 'schedule-added';
      }

      const url = await dispatch(createSubscriptionCheckoutSession({
        priceId,
        voucherCodes: [],
        intl,
        passActivation: true,
        annualPass: {
          code,
          annualPassLicensePlate,
        },
      }));

      if (url) {
        dispatch(activatePassWithSubscriptionSuccess());
        return url;
      }

      // No URL → the checkout thunk swallowed its error into the store
      // (reducer field `stripeCheckoutSessionError`). Refusals that a retry
      // can never fix get their own message, or "please try again" would loop
      // the customer forever.
      const checkoutError = getState().PassSettingsPage.stripeCheckoutSessionError;
      dispatch(
        activatePassWithSubscriptionError({
          code: toActivationFailureCode(checkoutError),
          validFrom: checkoutError?.validFrom,
        })
      );
    } else if (reason === 'not-yet-valid') {
      dispatch(activatePassWithSubscriptionError({ code: 'not-yet-valid', validFrom }));
    } else {
      const voucherIsExpired = dayjs.utc().isAfter(result?.expiration_date);
      if (voucherIsExpired) {
        dispatch(activatePassWithSubscriptionError({ code: 'expired' }));
      } else {
        dispatch(activatePassWithSubscriptionError({ code: 'invalid' }));
      }
    }
  } catch (e) {
    logError(e, 'activate-annual-pass-with-subscription', { code });
    dispatch(activatePassWithSubscriptionError(toThrownActivationFailure(e)));
  }
};

export const createSubscriptionCheckoutSession = ({
  priceId,
  voucherCodes = [],
  passActivation = false,
  returnUrl = window.location.href,
  intl,
  annualPass = null,
  source,
  campaign,
}) => async (dispatch, getState) => {
  const { currentUser } = getState().user;
  const { stripeCheckoutSessionInProgress } = getState().PassSettingsPage;

  if (stripeCheckoutSessionInProgress) return;

  try {
    dispatch(createStripeCheckoutSessionRequest());

    const stripeCustomer = currentUser.attributes.profile.privateData.stripeCustomer;
    if (!stripeCustomer) {
      const response = await stripeCreateCustomer({ user: currentUser });
      await dispatch(updateUserProfile({
        privateData: { stripeCustomer: response.data.stripeCustomer },
      }));
    }

    // Ignore the caller-supplied returnUrl path — we always anchor on the
    // current location so web + Cordova both route back to the same place.
    const cleanedReturnUrl = computeStripeReturnUrl();
    // Hosted Checkout drops the user back on this page, not on a thank-you page —
    // the purchase is fired server-side from the Stripe webhook, which needs
    // this consent context stored under the session id.
    const tracking = await collectTrackingContext();
    const response = await subscriptionCreateSession({
      priceId,
      voucherCodes: voucherCodes.map(c => c.code),
      successUrl: cleanedReturnUrl,
      returnUrl: cleanedReturnUrl,
      lableText: intl.formatMessage({ id: 'PassSettingsPage.duck.licencePlate' }),
      locale: intl.locale,
      originContext: isCordova() ? ORIGIN_CONTEXT.APP : ORIGIN_CONTEXT.WEB,
      checkoutType: CHECKOUT_TYPE.DIRECT,
      passActivation,
      annualPass,
      tracking,
      source,
      campaign,
    });

    dispatch(createStripeCheckoutSessionSuccess());
    return response.data.url;
  } catch (e) {
    logError(e, 'create-stripe-checkout-session');
    // Backend returns plain JSON for the 409 duplicate-active case
    // (`{ error: { code, subscriptionId } }`). postWeb's error path runs
    // `Object.assign(new Error(), data)`, so `e.error.code` is the path.
    // We surface the code via the error reducer — return value stays
    // "URL or undefined" to preserve the contract every other caller
    // (subscription modal, listing CTA) relies on.
    dispatch(createStripeCheckoutSessionError(e));
  }
};

/**
 * Hosted checkout for a SUBSCRIPTION-based 25Pass holder switching to a full
 * annual pass. The 25Pass subscription keeps running until the new pass is
 * actually paid — the server cancels it on fulfil, so an abandoned checkout
 * leaves the customer with the pass they already have.
 *
 * @type {(args: {intl: Intl, passType: string, source?: string, campaign?: string}) => import('../../../types/vansite').AppThunkAction<string|undefined>}
 */
export const createPassChangeCheckoutSession = ({ intl, passType, source, campaign }) => async (dispatch, getState) => {
  const { passChangeCheckoutInProgress } = getState().PassSettingsPage;
  if (passChangeCheckoutInProgress) return;

  dispatch(passChangeCheckoutRequest());
  try {
    const returnUrl = computeStripeReturnUrl();
    // Hosted Checkout kehrt auf diese Seite zurück, nicht auf eine Dankeseite —
    // der Purchase feuert serverseitig aus dem Stripe-Webhook und braucht den
    // Consent-Kontext unter der Session-ID. Wie in
    // createSubscriptionCheckoutSession / createNextPeriodCheckoutSession.
    const tracking = await collectTrackingContext();
    const response = await subscriptionPassChangeCheckout({
      passType,
      locale: intl.locale,
      successUrl: returnUrl,
      returnUrl,
      originContext: isCordova() ? ORIGIN_CONTEXT.APP : ORIGIN_CONTEXT.WEB,
      tracking,
      source,
      campaign,
    });
    dispatch(passChangeCheckoutSuccess());
    return response.data.url;
  } catch (e) {
    logError(e, 'create-pass-change-checkout-session');
    // The endpoint answers plain JSON on the 4xx/409 guards
    // (`{ error: { code } }`); postWeb rehydrates that onto the Error.
    dispatch(passChangeCheckoutError({ error: e?.error?.code || 'generic', raw: e }));
  }
};

/**
 * Hosted checkout for a CODE-pass holder (valid pass in the profile, no
 * subscription behind it) upgrading to a subscription at −30 €. Same endpoint the
 * checkout-page upgrade card uses; the server owns the eligibility guards and the
 * coupon, so this only passes the pass type through.
 *
 * No voucher codes: /api/subscription/upgrade-checkout takes none, so a code
 * entered here would silently do nothing — the caller must skip the voucher modal.
 *
 * @type {(args: {intl: Intl, passType: string, source?: string, campaign?: string}) => import('../../../types/vansite').AppThunkAction<string|undefined>}
 */
export const createPassUpgradeCheckoutSession = ({ intl, passType, source, campaign }) => async (dispatch, getState) => {
  const { passUpgradeCheckoutInProgress } = getState().PassSettingsPage;
  if (passUpgradeCheckoutInProgress) return;

  dispatch(passUpgradeCheckoutRequest());
  try {
    const returnUrl = computeStripeReturnUrl();
    // Hosted Checkout kehrt auf diese Seite zurück, nicht auf eine Dankeseite —
    // der Purchase feuert serverseitig aus dem Stripe-Webhook und braucht den
    // Consent-Kontext unter der Session-ID. Wie in createPassChangeCheckoutSession.
    const tracking = await collectTrackingContext();
    const response = await subscriptionUpgradeCheckout({
      passType,
      locale: intl.locale,
      successUrl: returnUrl,
      returnUrl,
      originContext: isCordova() ? ORIGIN_CONTEXT.APP : ORIGIN_CONTEXT.WEB,
      tracking,
      source,
      campaign,
    });
    dispatch(passUpgradeCheckoutSuccess());
    return response.data.url;
  } catch (e) {
    logError(e, 'create-pass-upgrade-checkout-session');
    // The endpoint answers plain JSON on its 4xx guards and on the 409
    // (`{ error: { code, subscriptionId } }`); postWeb rehydrates that onto the
    // Error. Keep both — `subscription-already-active` needs a different message
    // than a generic failure, and the id it names is the subscription the
    // reconcile dialog offers to adopt instead of leaving the user stuck.
    dispatch(
      passUpgradeCheckoutError({
        error: e?.error?.code || 'generic',
        subscriptionId: e?.error?.subscriptionId ?? null,
        raw: e,
      })
    );
  }
};

/**
 * Return-from-Stripe handler for the profile upgrade: link the new subscription
 * to the profile, then pull the profile + subscription it rewrote so the page
 * leaves the pre-checkout state without a reload. Mirrors fulfillNextPeriodSession.
 *
 * @type {(sessionId: string) => import('../../../types/vansite').AppThunkAction<void>}
 */
export const fulfillPassUpgradeSession = (sessionId) => async (dispatch, getState) => {
  const { passUpgradeFulfillInProgress } = getState().PassSettingsPage;
  if (passUpgradeFulfillInProgress) return;

  dispatch(passUpgradeFulfillRequest());
  try {
    await subscriptionUpgradeFulfill({ sessionId });
    dispatch(passUpgradeFulfillSuccess());
    await dispatch(fetchCurrentUser());
    await dispatch(fetchSubscriptions(true));
  } catch (e) {
    logError(e, 'fulfill-pass-upgrade-session');
    dispatch(passUpgradeFulfillError({ error: e?.error?.code || 'generic', raw: e }));
  }
};

/** @type {(sessionId: string) => import('../../../types/vansite').AppThunkAction<sting>} */
export const fulfillCheckoutSession = (sessionId) => async (dispatch, getState) => {
  const { fulfillStripeCheckoutSessionInProgress } = getState().PassSettingsPage;
  if (fulfillStripeCheckoutSessionInProgress) return;

  dispatch(fulfillStripeCheckoutSessionRequest());
  try {
    const result = await subscriptionFulfill({ sessionId });
    dispatch(fulfillStripeCheckoutSessionSuccess());
    // Pull the profile + subscription the fulfil just rewrote. Without this the
    // page keeps rendering the pre-checkout state until a manual reload — which
    // for the pass-change flow means the old 25Pass. Mirrors
    // fulfillNextPeriodSession.
    await dispatch(fetchCurrentUser());
    await dispatch(fetchSubscriptions(true));
    return result.data.success;
  } catch (e) {
    logError(e, 'fulfill-stripe-checkout-session');
    dispatch(fulfillStripeCheckoutSessionError(e));
  }
};

export const createNextPeriodCheckoutSession = ({ intl }) => async (dispatch, getState) => {
  const { nextPeriodCheckoutInProgress } = getState().PassSettingsPage;
  if (nextPeriodCheckoutInProgress) return;

  try {
    dispatch(nextPeriodCheckoutRequest());

    const currentUser = getState().user.currentUser;
    let stripeCustomer = currentUser.attributes.profile.privateData.stripeCustomer;
    if (!stripeCustomer) {
      const response = await stripeCreateCustomer({ user: currentUser });
      await dispatch(updateUserProfile({
        privateData: { stripeCustomer: response.data.stripeCustomer },
      }));
    }

    const returnUrl = computeStripeReturnUrl();
    const tracking = await collectTrackingContext();
    const response = await subscriptionNextPeriodCheckout({
      locale: intl.locale,
      successUrl: returnUrl,
      returnUrl: returnUrl,
      originContext: isCordova() ? ORIGIN_CONTEXT.APP : ORIGIN_CONTEXT.WEB,
      tracking,
    });

    dispatch(nextPeriodCheckoutSuccess());
    return response.data.url;
  } catch (e) {
    logError(e, 'create-next-period-checkout-session');
    dispatch(nextPeriodCheckoutError(e));
  }
};

// Shared by apply and remove: guard, call, then refetch so the annualPass
// metadata and the subscription reflect the server's coupon state.
const runRenewalVoucherRequest = (request, errorCode) => async (dispatch, getState) => {
  const { renewalVoucherInProgress } = getState().PassSettingsPage;
  if (renewalVoucherInProgress) return false;

  dispatch(renewalVoucherRequest());
  try {
    await request();
    dispatch(renewalVoucherSuccess());
    await dispatch(fetchCurrentUser());
    await dispatch(fetchSubscriptions(true));
    return true;
  } catch (e) {
    logError(e, errorCode);
    dispatch(renewalVoucherError(e));
    return false;
  }
};

export const applyRenewalVoucher = ({ voucherCodes }) =>
  runRenewalVoucherRequest(
    () => subscriptionRenewalVoucherApply({ voucherCodes }),
    'apply-renewal-voucher',
  );

export const removeRenewalVoucher = () =>
  runRenewalVoucherRequest(() => subscriptionRenewalVoucherRemove(), 'remove-renewal-voucher');

// Recovery flow for the orphaned-subscription case (Stripe sub exists but is
// not linked to the profile). Adopts the existing sub into the profile so the
// user does not pay for the next period twice. The caller passes the
// `subscriptionId` of the sub it displayed so the server can validate against
// the same target (eliminates client/server divergence on which sub to adopt).
export const reconcileExistingSubscription = (subscriptionId) => async (dispatch, getState) => {
  const { reconcileSubscriptionInProgress } = getState().PassSettingsPage;
  if (reconcileSubscriptionInProgress) return false;

  dispatch(reconcileSubscriptionRequest());
  try {
    await subscriptionReconcileExisting({ subscriptionId });
    dispatch(reconcileSubscriptionSuccess());
    await dispatch(fetchCurrentUser());
    await dispatch(fetchSubscriptions(true));
    return true;
  } catch (e) {
    logError(e, 'reconcile-existing-subscription');
    dispatch(reconcileSubscriptionError({ error: e?.error?.code || 'generic', raw: e }));
    return false;
  }
};

export const fulfillNextPeriodSession = (sessionId) => async (dispatch, getState) => {
  const { nextPeriodFulfillInProgress } = getState().PassSettingsPage;
  if (nextPeriodFulfillInProgress) return;

  dispatch(nextPeriodFulfillRequest());
  try {
    await subscriptionNextPeriodFulfill({ sessionId });
    dispatch(nextPeriodFulfillSuccess());
    await dispatch(fetchCurrentUser());
    await dispatch(fetchSubscriptions(true));
  } catch (e) {
    logError(e, 'fulfill-next-period-session');
    dispatch(nextPeriodFulfillError(e));
  }
};

export const loadData = (params, search, match) => async (dispatch, getState) => {
  const locale = getLocaleFromPath(match.path);
  await dispatch(fetchCurrentUser());
  return await Promise.all([
    dispatch(fetchSubscriptions()),
    dispatch(fetchSubscriptionProducts(locale)),
  ]);
};

// Injected lazily: the reducer reaches the store when this (code-split) module is evaluated.
reducerRegistry.register('PassSettingsPage', passSettingsPageReducer);
